/**
 * Access roles — the ones carried in the sign-in token (`app_metadata.roles`).
 *
 * Not to be confused with `profiles.roles` (sales_rep, inspector and the rest),
 * which say what somebody does on a job and grant nothing.
 *
 * Every question "does this caller hold role X" goes through `holdsRole`,
 * because one role answers yes to all of them: `super_admin`, held by Josh and
 * Jonathon (ADR 0152). The database asks the same question in `has_role()`, and
 * `supabase/functions/_shared/roles.ts` is a copy for the Edge Functions;
 * `supabase/test/super-admin.test.ts` holds all three to the same answers.
 */

/** Holds every other access role, including any added later. */
export const SUPER_ADMIN_ROLE = "super_admin";

/** The roles on a token, ignoring anything that is not a usable string. */
export function roleList(roles: unknown): string[] {
  if (!Array.isArray(roles)) return [];
  return roles.filter(
    (role): role is string => typeof role === "string" && role.trim() !== "",
  );
}

/** Does a caller holding `roles` hold `role`? A super admin holds them all. */
export function holdsRole(roles: unknown, role: string): boolean {
  const held = roleList(roles);
  return held.includes(SUPER_ADMIN_ROLE) || held.includes(role);
}

/**
 * Why a change to somebody's roles is refused, or null when it may go ahead.
 *
 * Only a super admin may grant or remove `super_admin` — otherwise any admin,
 * which is every member of staff, could make themselves one. Nobody may remove
 * their own `admin` or `super_admin`: that is the one change that can lock the
 * person making it out of the screen that would undo it.
 */
export function roleChangeRefusal(input: {
  callerRoles: unknown;
  isSelf: boolean;
  before: unknown;
  after: unknown;
}): string | null {
  const before = roleList(input.before);
  const after = roleList(input.after);
  const superBefore = before.includes(SUPER_ADMIN_ROLE);
  const superAfter = after.includes(SUPER_ADMIN_ROLE);

  if (
    superBefore !== superAfter &&
    !roleList(input.callerRoles).includes(SUPER_ADMIN_ROLE)
  ) {
    return "Only a super admin can grant or remove the super admin role.";
  }
  if (input.isSelf && superBefore && !superAfter) {
    return "You cannot remove your own super admin role.";
  }
  if (input.isSelf && !holdsRole(after, "admin")) {
    return "You cannot remove your own admin role.";
  }
  return null;
}

/**
 * Why deactivating or reactivating somebody is refused, or null.
 *
 * Only a super admin may change whether a super admin can sign in — otherwise
 * any admin could lock Josh or Jonathon out. Nobody changes their own.
 */
export function activationRefusal(input: {
  callerRoles: unknown;
  isSelf: boolean;
  targetRoles: unknown;
}): string | null {
  if (input.isSelf) return "You cannot deactivate yourself.";
  if (
    roleList(input.targetRoles).includes(SUPER_ADMIN_ROLE) &&
    !roleList(input.callerRoles).includes(SUPER_ADMIN_ROLE)
  ) {
    return "Only a super admin can deactivate or reactivate a super admin.";
  }
  return null;
}
